// src/components/RecepcionDigital/CardDetailDrawer.jsx
//
// ── Decisiones de diseño ──────────────────────────────────────────────────────
// Drawer lateral derecho para el DETALLE de una tarjeta — drawer y no modal
// (Principio 7: drawers para vistas de detalle; modales solo para acciones
// terminales). No bloquea el flujo: backdrop-click y Esc devuelven al operador
// a la grilla sin perder filtros ni scroll.
// - El hilo de WhatsApp se muestra completo pero de solo lectura: la edición y
//   el "usar como fuente" viven en Producción. Aquí el operador decide si la
//   encomienda merece abrirse ya, se pospone o se descarta.
// - "Descartar" es terminal → exige confirmación explícita en dos pasos con
//   copy inequívoco ("Esta acción no se puede deshacer.").
// - Acciones secundarias deshabilitadas mientras hay una pendiente (el mock API
//   simula latencia real) — Principio 6: estados de carga en toda superficie
//   asíncrona.
// - Colores por variables CSS del tema; alineación cliente-izquierda /
//   agente-derecha igual que el panel de Producción para consistencia visual.
// ──────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react';
import {
  X, Mic, Image as ImageIcon, Clock, Flag, TrendingUp, Trash2,
} from 'lucide-react';
import StatusChip from './shared/StatusChip';

const fmtTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')} ${hh}:${mm}`;
};

const ThreadMessage = ({ msg }) => {
  const isClient = msg.from === 'client';
  return (
    <div className={`flex ${isClient ? 'justify-start' : 'justify-end'}`}>
      <div
        className="max-w-[85%] rounded-lg border px-3 py-2"
        style={{
          background: isClient ? 'var(--bg)' : 'var(--blue-soft)',
          borderColor: 'var(--border)',
        }}
      >
        {msg.media_type === 'audio' && (
          <p className="flex items-center gap-1.5 text-xs font-medium" style={{ color: 'var(--text-main)' }}>
            <Mic size={12} aria-hidden="true" /> Nota de voz ({msg.duration_seconds || 0}s)
          </p>
        )}
        {msg.media_type === 'image' && (
          <p className="flex items-center gap-1.5 text-xs font-medium" style={{ color: 'var(--text-main)' }}>
            <ImageIcon size={12} aria-hidden="true" /> Foto adjunta
          </p>
        )}
        {msg.text && (
          <p className="text-xs leading-relaxed" style={{ color: 'var(--text-main)' }}>
            {msg.text}
          </p>
        )}
        <p className="mt-1 text-[10px]" style={{ color: 'var(--text-tertiary)' }}>
          {fmtTime(msg.timestamp)}
        </p>
      </div>
    </div>
  );
};

const CardDetailDrawer = ({ draft, open, onClose, onOpen, onAction, pendingAction, actionError }) => {
  const [confirmingDiscard, setConfirmingDiscard] = useState(false);

  // Esc cierra (primero la confirmación, luego el drawer)
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (confirmingDiscard) setConfirmingDiscard(false);
        else onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, confirmingDiscard, onClose]);

  useEffect(() => {
    if (!open) setConfirmingDiscard(false);
  }, [open]);

  if (!open || !draft) return null;

  const busy = Boolean(pendingAction);

  const secondaryBtn = (label, Icon, action) => (
    <button
      key={action}
      onClick={() => onAction(action, draft.id)}
      disabled={busy}
      aria-label={label}
      className="min-h-[44px] flex items-center gap-1.5 px-3 rounded-lg border text-xs font-medium transition-colors hover:opacity-90 focus-visible:outline focus-visible:outline-2 disabled:opacity-50"
      style={{
        background: 'var(--input-bg)',
        borderColor: 'var(--border)',
        color: 'var(--text-secondary)',
        outlineColor: 'var(--avianca-red)',
      }}
    >
      <Icon size={13} aria-hidden="true" />
      {pendingAction === action ? 'Un momento…' : label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-40" role="dialog" aria-modal="true" aria-label={`Detalle de la encomienda de ${draft.client?.name || 'cliente'}`}>
      {/* Backdrop */}
      <div
        className="absolute inset-0"
        style={{ background: 'rgba(0,0,0,0.35)' }}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Panel */}
      <aside
        className="absolute right-0 top-0 h-full w-[480px] max-w-full flex flex-col border-l"
        style={{ background: 'var(--tile)', borderColor: 'var(--border)' }}
      >
        {/* Cabecera */}
        <div className="flex items-start justify-between gap-3 border-b p-4" style={{ borderColor: 'var(--border)' }}>
          <div className="min-w-0">
            <p className="text-sm font-semibold truncate" style={{ color: 'var(--text-main)' }}>
              {draft.client?.name}
            </p>
            <p className="mt-0.5 text-xs" style={{ color: 'var(--text-secondary)' }}>
              {draft.client?.phone}
            </p>
            <div className="mt-2">
              <StatusChip status={draft.status} size="md" />
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Cerrar panel"
            className="min-h-[44px] min-w-[44px] flex items-center justify-center rounded-lg transition-colors hover:opacity-80 focus-visible:outline focus-visible:outline-2"
            style={{ color: 'var(--text-secondary)', outlineColor: 'var(--avianca-red)' }}
          >
            <X size={18} aria-hidden="true" />
          </button>
        </div>

        {/* Cuerpo scrolleable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-5">
          {/* Hilo completo */}
          <section aria-label="Conversación de WhatsApp">
            <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
              Conversación
            </h3>
            <div className="space-y-2">
              {(draft.whatsapp_thread || []).map((msg) => (
                <ThreadMessage key={msg.id} msg={msg} />
              ))}
              {(draft.whatsapp_thread || []).length === 0 && (
                <p className="text-xs italic" style={{ color: 'var(--text-tertiary)' }}>
                  Sin mensajes todavía.
                </p>
              )}
            </div>
          </section>

          {/* Resumen de paquetes */}
          <section aria-label="Resumen de la encomienda">
            <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide" style={{ color: 'var(--text-tertiary)' }}>
              Resumen de la encomienda
            </h3>
            {(draft.packages || []).length === 0 ? (
              <p className="text-xs italic" style={{ color: 'var(--text-secondary)' }}>
                El cliente todavía no declara destinatarios ni paquetes.
              </p>
            ) : (
              <ul className="space-y-2">
                {draft.packages.map((pkg) => {
                  const subtotal = (pkg.items || []).reduce(
                    (s, it) => s + (parseFloat(it.unit_value) || 0) * (parseInt(it.quantity) || 1), 0
                  );
                  return (
                    <li
                      key={pkg.unique_package_id}
                      className="rounded-lg border px-3 py-2 text-xs"
                      style={{ borderColor: 'var(--border)', background: 'var(--bg)' }}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-medium truncate" style={{ color: 'var(--text-main)' }}>
                          {pkg.recipient_name}
                        </span>
                        <span className="font-mono shrink-0" style={{ color: 'var(--text-secondary)' }}>
                          {pkg.unique_package_id}
                        </span>
                      </div>
                      <div className="mt-1 flex items-center justify-between" style={{ color: 'var(--text-secondary)' }}>
                        <span>
                          {(pkg.items || []).length}{' '}
                          {(pkg.items || []).length === 1 ? 'artículo' : 'artículos'}
                        </span>
                        <span className="font-mono">${subtotal.toFixed(2)}</span>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        </div>

        {/* Pie de acciones */}
        <div className="border-t p-4 space-y-3" style={{ borderColor: 'var(--border)' }}>
          {actionError && (
            <p className="text-xs" style={{ color: 'var(--avianca-red)' }} role="alert">
              {actionError}
            </p>
          )}

          {confirmingDiscard ? (
            <div
              className="rounded-lg border p-3"
              style={{ borderColor: 'var(--avianca-red)', background: 'var(--red-soft)' }}
              role="alertdialog"
              aria-label="Confirmar descarte de la encomienda"
            >
              <p className="text-xs font-medium" style={{ color: 'var(--text-main)' }}>
                ¿Descartar esta encomienda? Esta acción no se puede deshacer.
              </p>
              <div className="mt-3 flex gap-2">
                <button
                  onClick={() => setConfirmingDiscard(false)}
                  disabled={busy}
                  className="min-h-[44px] flex-1 rounded-lg border text-xs font-medium transition-colors hover:opacity-90 focus-visible:outline focus-visible:outline-2 disabled:opacity-50"
                  style={{ background: 'var(--tile)', borderColor: 'var(--border)', color: 'var(--text-main)', outlineColor: 'var(--avianca-red)' }}
                >
                  Cancelar
                </button>
                <button
                  onClick={() => onAction('discard', draft.id)}
                  disabled={busy}
                  className="min-h-[44px] flex-1 rounded-lg text-xs font-semibold text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 disabled:opacity-50"
                  style={{ background: 'var(--avianca-red)', outlineColor: 'var(--avianca-red)' }}
                >
                  {pendingAction === 'discard' ? 'Descartando…' : 'Descartar definitivamente'}
                </button>
              </div>
            </div>
          ) : (
            <>
              <button
                onClick={() => onOpen(draft.id)}
                disabled={busy}
                className="min-h-[44px] w-full rounded-lg text-sm font-semibold text-white transition-opacity hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 disabled:opacity-50"
                style={{ background: 'var(--avianca-red)', outlineColor: 'var(--avianca-red)' }}
              >
                Abrir en Producción
              </button>
              <div className="flex flex-wrap gap-2">
                {secondaryBtn('Posponer', Clock, 'snooze')}
                {secondaryBtn('Marcar para seguimiento', Flag, 'follow_up')}
                {secondaryBtn('Escalar', TrendingUp, 'escalate')}
                <button
                  onClick={() => setConfirmingDiscard(true)}
                  disabled={busy}
                  aria-label="Descartar encomienda"
                  className="min-h-[44px] flex items-center gap-1.5 px-3 rounded-lg border text-xs font-medium transition-colors hover:opacity-90 focus-visible:outline focus-visible:outline-2 disabled:opacity-50"
                  style={{ background: 'var(--red-soft)', borderColor: 'var(--avianca-red)', color: 'var(--avianca-red)', outlineColor: 'var(--avianca-red)' }}
                >
                  <Trash2 size={13} aria-hidden="true" />
                  Descartar
                </button>
              </div>
            </>
          )}
        </div>
      </aside>
    </div>
  );
};

export default CardDetailDrawer;
